import { Grid, Typography } from '@mui/material';
import { tokens, useTranslation } from '../../utils/i18n';
import { normalizeOsvMoney } from '../../utils/helpers/osvMoney';

const formatNum = (num: number) =>
  new Intl.NumberFormat('kz-KZ', {
    // style: 'currency',
    // currency: 'KZT',
    maximumFractionDigits: 2
    // normalizeOsvMoney округляет до 2 знаков и убирает артефакт «-0»/микроостаток float
    // (например -3.47e-18 при свёрнутых дочерних помещениях), иначе итог «Долг» показывал «-0»
  }).format(normalizeOsvMoney(num));

// OSI-808: раньше при значении 0 возвращалось ЧИСЛО 0 в обход форматтера. Визуально это
// не отличалось (formatNum(0) === "0"), но тот же ?: глушил undefined/null до нуля,
// маскируя отсутствие данных.
const getTotal = (totals: any[], formatFunc: any) => (index: number) => {
  const value = totals?.[index];
  return typeof value === 'number' && Number.isFinite(value) ? formatFunc(value) : formatFunc(0);
};

interface Props {
  totals: any;
}

// OSI-808: подвал рендерится ТОЛЬКО там, где нет закреплённой строки «ИТОГО» (вкладки
// «Помещения»): на экранах с totalRows он дублировал бы её теми же шестью числами, поэтому
// OsvGrid скрывает его через hideFooter. Переключатель категорий живёт не здесь, а в панели
// над таблицей (DataGridToolBar) — вместе с подвалом он бы исчезал.
export const StateFooter: React.FC<Props> = ({ totals }) => {
  const total = getTotal(totals, formatNum);
  const { translateToken: tt } = useTranslation();

  // OSI-808: у getOsvRows массив totals из 6 элементов — [begin, sumOfAccurals, sumOfFixes,
  // kredit, end, sumOfFines], т.е. индекс 4 = Долг, индекс 5 = Пеня. У getOsvRowsByFlat
  // (вкладка «Помещения») массив из 5 элементов и БЕЗ пени — там футер остаётся прежним.
  const hasFine = Array.isArray(totals) && totals.length > 5 && typeof totals[5] === 'number';
  const firstColMd = hasFine ? 2 : 3;

  return (
    // OSI-808: раскладка футера на узких экранах. Было xs={12} у каждой подписи — ниже ~900px
    // все шесть подписей вставали в столбец и съедали ~200px высоты (второй источник того же
    // дефекта «итоги закрыли таблицу»). Теперь xs={6} (две колонки) и sm={4} (три колонки),
    // на md раскладка ровно прежняя — одна строка. Отступы на узких экранах тоже уменьшены.
    <Grid container spacing={{ xs: 0.5, md: 2 }} direction="row" alignItems="center" justifyContent="flex-end">
      <Grid item xs={6} sm={4} md={firstColMd}>
        <StateItem title={tt(tokens.osiOSV.table.footer.startOsv)} value={total(0)} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StateItem title={tt(tokens.osiOSV.table.footer.accrued)} value={total(1)} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StateItem title={tt(tokens.osiOSV.table.footer.fixes)} value={total(2)} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StateItem title={tt(tokens.osiOSV.table.footer.payed)} value={total(3)} />
      </Grid>
      {/* OSI-808: «Пеня» стоит ПЕРЕД «Долгом», чтобы порядок подписей футера совпал
          с порядком колонок таблицы (…Оплачено, Пеня, Долг), несмотря на обратный
          порядок этих значений в массиве totals (4 = Долг, 5 = Пеня). */}
      {hasFine && (
        <Grid item xs={6} sm={4} md={2}>
          <StateItem title={tt(tokens.osiOSV.table.footer.fine)} value={total(5)} />
        </Grid>
      )}
      <Grid item xs={6} sm={4} md={2}>
        <StateItem title={tt(tokens.osiOSV.table.footer.debt)} value={total(4)} />
      </Grid>
    </Grid>
  );
};

interface StateItemProps {
  title: string;
  value: any;
}

// OSI-808: на узких экранах уменьшены горизонтальные отступы и размер шрифта, чтобы подпись
// со значением помещалась в половину ширины и футер не рос в высоту. На md и шире —
// прежние значения (pl/pr = 2, размер шрифта по умолчанию).
const StateItem: React.FC<StateItemProps> = ({ title, value }) => (
  <>
    <Typography
      component="span"
      sx={{ pl: { xs: 0.5, md: 2 }, pr: { xs: 0.5, md: 2 }, fontSize: { xs: '0.75rem', md: 'inherit' } }}
    >
      {title}
    </Typography>
    <Typography component="span" sx={{ color: 'primary.main', fontSize: { xs: '0.75rem', md: 'inherit' } }}>
      {value}
    </Typography>
  </>
);
